import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { keyBy, keys, orderBy, values } from 'lodash';
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
  Text,
  Flex,
  Card,
} from '@chakra-ui/react';
import DynamicTable from '../table/DynamicTable';
import { allIcons, CirlceIcon, icons, NormalIcon } from '../utils/Icon';
import HistoryTable from './HistoryTable';
import { axiosInstance } from '../../api/init';
import RdvTable from './RdvTable';
import { TState, useActions } from '../../redux/store';
import { useCanI, useSubTabs } from '../../redux/actions';
import CommentsTab from './CommentsTab';
import { mergeWithArray } from '../../helpers';
import PDFGenerationTab from './PDFGenerationTab';
import FileTab from '../files/FileTab';

type TSubTabs = {
  forms?: JSX.Element[];
  errs?: Record<string, any>;
};

export const useHandleUpload = () => {
  const toast = useToast();
  const rowData = useSelector((state: TState) => state.rowData);

  return async (files: FileList, id: string) => {
    if (!files) {
      alert('Please select a file first!');
      return;
    }

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    formData.append('id', id);

    try {
      toast({
        title: 'Import en cours',
        status: 'warning',
      });

      const response = await axiosInstance.post(
        `/api/file/${rowData._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      toast({
        title: `${response.data.message}`,
        status: 'success',
      });
    } catch (error) {
      console.error('Error uploading the file', error);
    }
  };
};

// const iconStyle = {
//   color: 'inherit',
//   _hover: { color: 'inherit', borderColor: 'red' },
//   boxSize: 21,
// };

export const useGetCorrectIndex = () => {
  const subTabs = useSubTabs();
  const correctSubTabs = [...subTabs].slice(1);

  return (index: number) => {
    const newIndex = (correctSubTabs.length || 0) + index;
    return isNaN(index) ? null : index == null ? 0 : newIndex;
  };
};

export const useGetCorrectTab = () => {
  const modalData = useSelector((state: TState) => state.modalData);
  const subTabs = useSubTabs();
  const correctSubTabs = [...subTabs].slice(1);

  const [state, setState] = useState<any>();

  useEffect(() => {
    const tabIndex = modalData?.tabIndex || 0;
    setState(correctSubTabs[tabIndex] || null);
  }, [modalData?.tabIndex, correctSubTabs?.length]);

  return state;
};

const SubTabs = ({ forms, errs }: TSubTabs) => {
  const appointsHistory = useSelector((state: TState) => state.appointsHistory);
  const rowData = useSelector((state: TState) => state.rowData);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const rowHistory = useSelector((state: TState) => state.rowHistory);
  const isPlanning = useSelector((state: TState) => state.isPlanning);
  const isEditMode = useSelector((state: TState) => state.isEditMode);
  const modalType = useSelector((state: TState) => state.modalType);
  const modalData = useSelector((state: TState) => state.modalData);
  const user = useSelector((state: TState) => state.user);
  const theme = useSelector((state: TState) => state.theme);

  const { setModalType, setTabToUpdate, setModalData } = useActions();
  const subTabs = useSubTabs();
  const canI = useCanI();

  const errosByTabId = keyBy(errs, 'tabId');

  const groupedForms = forms?.reduce(
    (prev, curr) =>
      mergeWithArray(prev, {
        [curr.props?.field.parent_id]: {
          [curr.props?.field.container_id]: [curr],
        },
      }),
    {} as Record<string, Record<string, JSX.Element[]>>,
  );

  const isAdd = modalType === 'addEntry';

  const [tabIndex, setTabIndex] = useState(0);

  const correctSubTabs = [...subTabs].slice(1);

  const handleTabChange = (index: number) => {
    if (isAdd) {
      setTabIndex(index);
    } else {
      setModalData((md: any) => ({ ...md, tabIndex: index }));
    }
  };

  useEffect(() => {
    if (isAdd) {
      setModalData((md: any) => ({ ...md, tabIndex: 0 }));
    }
  }, [isAdd]);

  useEffect(() => {
    setTabIndex(0);
    setTabToUpdate(currentTab);
  }, [currentTab]);

  useEffect(() => {
    if (!isNaN(modalData?.tabIndex) && !isAdd) {
      setTabIndex(modalData?.tabIndex ?? 0);
    }
  }, [modalData?.tabIndex]);

  const isBasicPlan = user.plan === 'basic';
  const isProLightPlan = user.plan === 'proLight';

  const canHistory = canI('history', 'read');
  const canGeneratePdf = canI('pdf', 'add');
  const canReadPdf = canI('pdf', 'add');
  const canPdf =
    (canGeneratePdf || canReadPdf) && !isBasicPlan && !isProLightPlan;

  return !tabToUpdate || !rowData ? null : (
    <Box position='relative' height='100%' overflow='hidden'>
      <Tabs
        isLazy
        onChange={handleTabChange}
        index={tabIndex}
        display='flex'
        flexDirection='column'
        height='100%'
      >
        <TabList flexWrap='wrap' border='none'>
          {correctSubTabs?.map((tab, idx) => (
            <Tab key={tab?._id} textTransform='capitalize'>
              {tab?.icon && <NormalIcon as={allIcons[tab?.icon]} />}
              {tab?.title}
              {idx !== 0 && isEditMode && (
                <>
                  <CirlceIcon
                    as={icons.edit}
                    ml={3}
                    onClick={() => {
                      setTabToUpdate(subTabs[idx]);
                      setModalType('editTab');
                    }}
                  />
                  <CirlceIcon
                    as={icons.cross}
                    onClick={() => {
                      setModalType('delTab');
                    }}
                  />
                </>
              )}
              {keys(errosByTabId).includes(tab._id) && (
                <NormalIcon
                  as={allIcons.HiMiniExclamationTriangle}
                  fill='red.500'
                />
              )}
            </Tab>
          ))}
          {!isAdd && (
            <>
              <Tab textTransform='capitalize'>
                <NormalIcon as={allIcons.HiChatBubbleLeftRight} />
                Commentaires
              </Tab>
              <Tab textTransform='capitalize'>
                <NormalIcon as={allIcons.HiDocumentText} />
                Documents
              </Tab>
              {isPlanning && (
                <Tab textTransform='capitalize'>
                  <NormalIcon as={allIcons.HiCalendarDays} />
                  RDV
                </Tab>
              )}
              {canHistory && (
                <Tab textTransform='capitalize'>
                  <NormalIcon as={allIcons.HiClock} />
                  Historique
                </Tab>
              )}
              {canPdf && (
                <Tab textTransform='capitalize'>
                  <NormalIcon as={allIcons.HiClipboardDocumentList} />
                  PDFs
                </Tab>
              )}
            </>
          )}
          {isAdd && (
            <Text
              fontSize={theme.fontSize.small}
              p={2}
              borderRadius='5px'
              bg={theme.dark}
              color={theme.none}
            >
              Sauvegardez le dossier avant d'importer des documents
            </Text>
          )}
        </TabList>
        <TabPanels id='subTabsPanels' overflow='auto' flexGrow={1}>
          {correctSubTabs?.map((tab) => (
            <TabPanel key={tab?._id} p={0}>
              <Box py={5} border='none'>
                {/addEntry|editEntry/.test(modalType) ? (
                  values(groupedForms?.[`${tab?._id}`] || []).map(
                    (inputs, idx) => (
                      <Card w='100%' p={5} mb={5} bgColor='#f9fafb'>
                        <Flex
                          direction='row'
                          flexWrap='wrap'
                          w='100%'
                          borderRadius='10px'
                          columnGap='16px'
                          rowGap={1}
                          alignItems='flex-end'
                        >
                          {tab.tableColumns?.[idx]?.title && (
                            <Text
                              w='100%'
                              fontSize={theme.fontSize.header}
                              fontWeight='500'
                            >
                              {tab.tableColumns?.[idx]?.title}
                            </Text>
                          )}
                          {inputs.map((input) => (
                            <Box
                              w={[
                                '100%',
                                '100%',
                                '100%',
                                input.props.field.type === 'address'
                                  ? 'calc(100% - 16px)'
                                  : 'calc(100% - 16px)', //'calc(33.33% - 16px)'
                              ]}
                            >
                              {input}
                            </Box>
                          ))}
                        </Flex>
                      </Card>
                    ),
                  )
                ) : (
                  <DynamicTable tab={tab} />
                )}
              </Box>
            </TabPanel>
          ))}
          {!isAdd && (
            <TabPanel h='100%' overflow='hidden'>
              <CommentsTab />
            </TabPanel>
          )}
          {!isAdd && (
            <TabPanel>
              <FileTab />
            </TabPanel>
          )}
          {!isAdd && isPlanning && (
            <TabPanel>
              <RdvTable
                data={orderBy(appointsHistory[rowData._id], 'planningDate')}
                tabIndex={tabIndex}
              />
            </TabPanel>
          )}
          {!isAdd && canHistory && (
            <TabPanel>
              <HistoryTable data={rowHistory[rowData._id]} />
            </TabPanel>
          )}
          {!isAdd && canPdf && (
            <TabPanel>
              <PDFGenerationTab />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SubTabs;
